import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layouts/PageLayout';
import ImplementationDetailsPage from './ImplementationDetailsPage';
import BreadcrumbDropdown from '../../components/atoms/BreadcrumbDropdown/BreadcrumbDropdown';
import api from '../../services/api';
import { timeAgo } from '../../utils/timeAgo';

const ImplementationDetailsPageWrapper = () => {
  const { projectId, executionId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [execution, setExecution] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  // Note: tickets dropdown removed to prevent excessive Jira API calls

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, executionId]);

  // Clear executions when execution changes so dropdown refetches
  useEffect(() => {
    setExecutions([]);
  }, [executionId]);

  const fetchData = async () => {
    try {
      const [projectData, executionData] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/executions/${executionId}`)
      ]);
      setProject(projectData.project || projectData);
      setExecution(executionData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchExecutions = async () => {
    if (!execution?.ticketKey || !projectId) {
      // If execution not loaded yet, try to use current execution
      if (execution) {
        setExecutions([execution]);
      }
      return;
    }
    
    setLoadingExecutions(true);
    try {
      // Fetch all executions for the project and filter by ticketKey and type
      const data = await api.get(`/executions?projectId=${projectId}&limit=100`);
      console.log('🔍 [ImplementationDropdown] Fetched executions:', {
        total: data.executions?.length || 0,
        ticketKey: execution.ticketKey,
        projectId
      });
      
      const allExecutions = data.executions || [];
      console.log('🔍 [ImplementationDropdown] All executions:', allExecutions.map(e => ({
        executionId: e.executionId,
        ticketKey: e.ticketKey,
        type: e.type,
        name: e.name,
        createdAt: e.createdAt
      })));
      
      const ticketExecutions = allExecutions
        .filter(exec => {
          const matches = exec.ticketKey === execution.ticketKey && 
            (exec.type === 'ticket_implementation' || exec.type === 'implementation');
          if (!matches) {
            console.log('🔍 [ImplementationDropdown] Filtered out:', {
              executionId: exec.executionId,
              ticketKey: exec.ticketKey,
              expectedTicketKey: execution.ticketKey,
              type: exec.type
            });
          }
          return matches;
        })
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)); // Sort by newest first
      
      console.log('🔍 [ImplementationDropdown] Filtered ticket executions:', ticketExecutions.length);
      
      // Always include current execution if not already in list
      const hasCurrent = ticketExecutions.some(e => e.executionId === executionId);
      if (!hasCurrent && execution) {
        ticketExecutions.unshift(execution);
      }
      
      console.log('🔍 [ImplementationDropdown] Final executions list:', ticketExecutions.length);
      setExecutions(ticketExecutions);
    } catch (error) {
      console.error('❌ Error fetching executions:', error);
      // Fallback to current execution
      if (execution) {
        setExecutions([execution]);
      }
    } finally {
      setLoadingExecutions(false);
    }
  };

  const handleExecutionClick = (exec) => {
    const path = exec.type === 'analysis' 
      ? `/projects/${projectId}/analysis/${exec.executionId}`
      : `/projects/${projectId}/implementations/${exec.executionId}`;
    navigate(path);
  };

  const breadcrumbs = [
    { label: 'Projects', path: '/projects' },
    ...(project ? [{ label: project.name, path: `/projects/${projectId}`, projectId, isProject: true, isTicketsPage: true }] : []),
    ...(execution ? [{ label: execution.ticketKey, path: `/projects/${projectId}/tickets/${execution.ticketKey}` }] : []),
    { 
      label: (
        <BreadcrumbDropdown
          label="Implementation"
          isActive={false}
          onFetch={fetchExecutions}
          items={executions}
          loading={loadingExecutions}
          onItemClick={handleExecutionClick}
          getItemLabel={(item) => {
            const name = item.name || `${item.type === 'analysis' ? 'Analysis' : 'Implementation'}`;
            const time = item.createdAt ? timeAgo(item.createdAt) : '';
            return time ? `${name} - ${time}` : name;
          }}
          getItemKey={(item) => item.executionId}
          emptyMessage="No executions"
          headerLabel="EXECUTIONS"
        />
      ),
      path: ''
    },
    ...(execution?.name ? [{ label: execution.name, path: '' }] : [])
  ];

  return (
    <PageLayout 
      title={execution?.ticketKey || 'Implementation'}
      breadcrumbs={breadcrumbs}
    >
      <ImplementationDetailsPage setExecutionName={() => {}} />
    </PageLayout>
  );
};

export default ImplementationDetailsPageWrapper;
